import { useState } from 'react';
import type { Customer, EndConsumerSearch } from '@metron/shared';
import { endConsumerSearchSchema } from '@metron/shared';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { CustomerPicker } from '../CustomerPicker.tsx';
import { type FilterChipItem, FilterChipList } from '../search/FilterChipList.tsx';

/** Spec fields a user can add or edit here; paging and sort belong to the table. */
type EndConsumerFilterField =
  | 'customerId'
  | 'text'
  | 'meterCountMin'
  | 'meterCountMax'
  | 'movedInWithinDays';

export interface EndConsumerFilterChip extends FilterChipItem<EndConsumerFilterField> {
  /** Fields cleared when the chip is removed (the meter range shares one chip). */
  fields: EndConsumerFilterField[];
}

/** One chip per active filter, in a fixed order. The customer shows by name, never by id. */
export function describeEndConsumerFilters(
  spec: EndConsumerSearch,
  customerName?: string,
): EndConsumerFilterChip[] {
  const chips: EndConsumerFilterChip[] = [];
  if (spec.customerId !== undefined) {
    chips.push({
      id: 'customerId',
      label: `Customer: ${customerName ?? 'selected customer'}`,
      fields: ['customerId'],
      editable: true,
    });
  }
  if (spec.text !== undefined) {
    chips.push({ id: 'text', label: `Matches “${spec.text}”`, fields: ['text'], editable: true });
  }
  const { meterCountMin: min, meterCountMax: max } = spec;
  const range = min !== undefined && max !== undefined
    ? `${min}–${max} meters`
    : min !== undefined
    ? `≥ ${min} meters`
    : max !== undefined
    ? `≤ ${max} meters`
    : null;
  if (range) {
    chips.push({
      id: 'meterCountMin',
      label: range,
      fields: ['meterCountMin', 'meterCountMax'],
      editable: true,
    });
  }
  if (spec.movedInWithinDays !== undefined) {
    const d = spec.movedInWithinDays;
    chips.push({
      id: 'movedInWithinDays',
      label: `Moved in within ${d} day${d === 1 ? '' : 's'}`,
      fields: ['movedInWithinDays'],
      editable: true,
    });
  }
  return chips;
}

/** Drop the given filter fields and return to page 1. */
export function withoutEndConsumerFields(
  spec: EndConsumerSearch,
  fields: EndConsumerFilterField[],
): EndConsumerSearch {
  const next: EndConsumerSearch = { ...spec, page: 1 };
  for (const f of fields) delete next[f];
  return next;
}

interface EditorProps {
  spec: EndConsumerSearch;
  customers: Customer[];
  customersLoading: boolean;
  focusField: EndConsumerFilterField | null;
  onApply: (next: EndConsumerSearch) => void;
}

function EndConsumerFilterEditor(
  { spec, customers, customersLoading, focusField, onApply }: EditorProps,
) {
  const [customerId, setCustomerId] = useState<number | undefined>(spec.customerId);
  const [text, setText] = useState(spec.text ?? '');
  const [min, setMin] = useState(
    spec.meterCountMin === undefined ? '' : String(spec.meterCountMin),
  );
  const [max, setMax] = useState(
    spec.meterCountMax === undefined ? '' : String(spec.meterCountMax),
  );
  const [movedIn, setMovedIn] = useState(
    spec.movedInWithinDays === undefined ? '' : String(spec.movedInWithinDays),
  );
  const [errors, setErrors] = useState<string[]>([]);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const num = (raw: string) => (raw.trim() === '' ? undefined : Number(raw));
    const candidate: Record<string, unknown> = {
      ...spec,
      page: 1,
      customerId,
      text: text.trim() === '' ? undefined : text,
      meterCountMin: num(min),
      meterCountMax: num(max),
      movedInWithinDays: num(movedIn),
    };
    const parsed = endConsumerSearchSchema.safeParse(
      Object.fromEntries(Object.entries(candidate).filter(([, v]) => v !== undefined)),
    );
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onApply(parsed.data);
  };

  return (
    <form onSubmit={apply} className='space-y-3' aria-label='Edit filters'>
      <div className='space-y-1'>
        <Label htmlFor='end-consumer-search-customer'>Customer</Label>
        <CustomerPicker
          inputId='end-consumer-search-customer'
          customers={customers}
          isLoading={customersLoading}
          selectedCustomerId={customerId}
          onSelect={setCustomerId}
          onClear={() => setCustomerId(undefined)}
        />
      </div>
      <div className='space-y-1'>
        <Label htmlFor='end-consumer-search-text'>Email or name</Label>
        <Input
          id='end-consumer-search-text'
          value={text}
          autoFocus={focusField === 'text'}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1'>
          <Label htmlFor='end-consumer-search-meterCountMin'>Meters at least</Label>
          <Input
            id='end-consumer-search-meterCountMin'
            type='number'
            inputMode='numeric'
            min={0}
            step={1}
            value={min}
            autoFocus={focusField === 'meterCountMin'}
            onChange={(e) => setMin(e.target.value)}
          />
        </div>
        <div className='space-y-1'>
          <Label htmlFor='end-consumer-search-meterCountMax'>Meters at most</Label>
          <Input
            id='end-consumer-search-meterCountMax'
            type='number'
            inputMode='numeric'
            min={0}
            step={1}
            value={max}
            onChange={(e) => setMax(e.target.value)}
          />
        </div>
      </div>
      <div className='space-y-1'>
        <Label htmlFor='end-consumer-search-movedInWithinDays'>Moved in within (days)</Label>
        <Input
          id='end-consumer-search-movedInWithinDays'
          type='number'
          inputMode='numeric'
          min={1}
          step={1}
          value={movedIn}
          autoFocus={focusField === 'movedInWithinDays'}
          onChange={(e) => setMovedIn(e.target.value)}
        />
      </div>
      {errors.length > 0 && (
        <ul role='alert' className='text-sm text-destructive list-disc pl-5'>
          {/* Replaced wholesale on each submit, and two issues can share a message. */}
          {errors.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      <div className='flex justify-end'>
        <Button type='submit' size='sm'>Apply</Button>
      </div>
    </form>
  );
}

interface EndConsumerSearchFiltersProps {
  spec: EndConsumerSearch;
  customers: Customer[];
  customersLoading: boolean;
  onChange: (next: EndConsumerSearch) => void;
}

/** Active end consumer filters as chips plus an Add filter editor. */
export function EndConsumerSearchFilters(
  { spec, customers, customersLoading, onChange }: EndConsumerSearchFiltersProps,
) {
  const customerName = customers.find((c) => c.id === spec.customerId)?.waterscopeAccountName;
  return (
    <FilterChipList
      chips={describeEndConsumerFilters(spec, customerName)}
      emptyText='No filters: showing all end consumers.'
      onRemove={(chip) => onChange(withoutEndConsumerFields(spec, chip.fields))}
      renderEditor={(focusField, close) => (
        <EndConsumerFilterEditor
          spec={spec}
          customers={customers}
          customersLoading={customersLoading}
          focusField={focusField}
          onApply={(next) => {
            close();
            onChange(next);
          }}
        />
      )}
    />
  );
}
